<script>
import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav";
import pagination from "@/mixins/pagination";
import slotService from "@/services/slot";
import helperService from "@/services/helper";
import ModalAppointment from "@/components/appointment/ModalAppointment";
import ModalImageReports from "@/components/appointment/ModalImageReports";
import moment from "moment";
import {
  getPatientAdmissionStatus,
  WEEK_DAYS_OPTIONS,
} from "@/services/constants";
import { mapGetters } from "vuex";
import parametrizationService from "@/services/parametrization";
import appointmentService from "@/services/appointment";
// import indicationService from "@/services/indication";
import {
  isObservationsEnabled,
  isAdmissionForDeragopyanEnabled,
  isAdmissionTimestampEnabled,
} from "@/services/config";

export default {
  name: "ScheduleView",

  mixins: [pagination],

  components: {
    Spinner,
    BaseSubNav,
    ModalAppointment,
    ModalImageReports,
  },

  data() {
    return {
      isLoading: false,
      slots: [],
      filterByOccupied: true,
      selectedSlot: undefined,
      isModalVisible: false,
      selectedDate: new Date(),
      isCallProviderEnabled: false,
      isDirectAdmissionEnabled: false,
      loadingCalls: {},
      isObservationsEnabled,
      isAdmissionForDeragopyanEnabled,
      pollingInterval: null,
      inactivityTimer: null,
      inactivityRefreshDelay: 60000,
      activityEvents: [
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
      ],
      isAdmissionTimestampEnabled,
      imageReportsModal: {
        visible: false,
        patientId: null,
        patientName: "",
      },
      patientImageResultsMap: {},
    };
  },

  computed: {
    ...mapGetters(["isDoctor", "isAdmin", "isAdmissionist"]),
    filteredSlots() {
      return this.slots.filter((slot) => {
        const slotDate = moment(slot.startsAt).startOf("day").toISOString();
        const selectedDate = moment(this.selectedDate)
          .startOf("day")
          .toISOString();
        return slotDate === selectedDate;
      });
    },
    isToday() {
      return moment(this.selectedDate).isSame(moment(), "day");
    },

    formattedDate() {
      const selectedDate = moment(this.selectedDate);

      if (this.isToday) {
        return `Hoy ${selectedDate.format("DD/MM")}`;
      }

      const weekday = WEEK_DAYS_OPTIONS[selectedDate.day()].name;
      return `${weekday} ${selectedDate.format("DD/MM")}`;
    },

    isImageReportsButtonVisible() {
      return this.isDoctor || this.isAdmin || this.isAdmissionist;
    },
  },

  watch: {
    filterByOccupied() {
      this.getTodaySlots();
    },
    selectedDate() {
      this.getTodaySlots();
    },
  },

  async created() {
    this.getTodaySlots();
    await Promise.all([
      this.checkCallProviderEnabled(),
      this.checkDirectAdmissionEnabled(),
    ]);
    if (this.isAdmissionForDeragopyanEnabled) {
      this.pollingInterval = setInterval(this.refreshSlotsStatus, 20000);
    }

    this.startInactivityRefresh();
  },

  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.stopInactivityRefresh();
  },

  methods: {
    onPageChange() {
      this.getTodaySlots();
    },

    startInactivityRefresh() {
      this.activityEvents.forEach((event) =>
        window.addEventListener(event, this.resetInactivityTimer)
      );
      this.resetInactivityTimer();
    },

    stopInactivityRefresh() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }

      this.activityEvents.forEach((event) =>
        window.removeEventListener(event, this.resetInactivityTimer)
      );
    },

    resetInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }

      this.inactivityTimer = setTimeout(() => {
        console.log(
          "[inactivity] Refresh disparado:",
          new Date().toLocaleTimeString()
        );
        this.getTodaySlots();
        this.resetInactivityTimer();
      }, this.inactivityRefreshDelay);
    },

    openImageReports(slot) {
      const patient = slot.appointment?.patient;
      if (!patient) return;
      this.imageReportsModal.patientId = patient._id;
      this.imageReportsModal.patientName = this.getFullName(patient);
      this.imageReportsModal.visible = true;
    },

    changeDate(days) {
      const newDate = new Date(this.selectedDate);
      newDate.setDate(newDate.getDate() + days);
      this.selectedDate = newDate;
    },

    goToToday() {
      if (this.isToday) return;
      this.selectedDate = new Date();
    },

    openDatePicker() {
      if (this.$refs.datePicker) {
        this.$refs.datePicker.focus();
      }
    },

    async getTodaySlots() {
      this.isLoading = true;
      const startOfDay = moment(this.selectedDate).startOf("day").toISOString();
      const endOfDay = moment(this.selectedDate).endOf("day").toISOString();

      const slots = await slotService.getSlotsByDoctor({
        doctorId: this.doctorId(),
        params: {
          startsAt: { $gte: startOfDay },
          endsAt: { $lte: endOfDay },
          ...(this.filterByOccupied && { appointment: { $exists: true } }),
          populate: [
            {
              path: "appointment",
              populate: "patient",
            },
            { path: "medicalSpecialties institution doctor" },
          ],
          offset: this.pagination.offset,
          limit: this.pagination.limit,
        },
      });

      this.slots = slots.docs;
      this.pagination.total = slots.total || 0;
      this.isLoading = false;
      // this.loadPatientImageResults(slots.docs);
    },

    // This was commented out because it was impacting the performance, we have to refactor it
    // async loadPatientImageResults(slots) {
    //   const patientIds = [
    //     ...new Set(
    //       slots
    //         .filter((s) => s.appointment?.patient?._id)
    //         .map((s) => s.appointment.patient._id)
    //     ),
    //   ];
    //   const results = await Promise.all(
    //     patientIds.map((id) =>
    //       indicationService
    //         .get(id, [{ type: "imagesResults" }])
    //         .then((inds) => ({ id, hasResults: inds.length > 0 }))
    //         .catch(() => ({ id, hasResults: false }))
    //     )
    //   );
    //   const map = {};
    //   for (const { id, hasResults } of results) {
    //     map[id] = hasResults;
    //   }
    //   this.patientImageResultsMap = map;
    // },

    getAttentionType(slot) {
      const attentionTypeLabels = {
        virtual: "Virtual",
        "in-person": "Presencial",
      };

      if (slot.appointment?.attentionType) {
        return (
          attentionTypeLabels[slot.appointment.attentionType] || "Indefinido"
        );
      }

      if (Array.isArray(slot.attentionType) && slot.attentionType.length > 0) {
        return slot.attentionType
          .map((type) => attentionTypeLabels[type] || "Indefinido")
          .join(", ");
      }

      return "Indefinido";
    },

    handleSlot(slot) {
      if (slot.appointment) {
        this.$router.push({
          name: "appointment",
          params: {
            appointmentId: slot.appointment._id,
          },
        });
      } else {
        if (slot.available) {
          this.showModal(slot);
        }
      }
    },

    showModal(slot) {
      this.isModalVisible = true;
      this.selectedSlot = slot;
    },

    closeModal() {
      this.isModalVisible = false;
    },

    isRoomStarted(slot) {
      return slot.appointment && slot.appointment.status === "started";
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    truncateObservation(observation) {
      if (!observation) return "";
      return observation.length > 100
        ? observation.substring(0, 100) + "..."
        : observation;
    },

    doctorId() {
      return this.$store.state.user._id;
    },

    getMedicalSpecialtiesNames(slot) {
      return slot.medicalSpecialties?.map((m) => m?.name).join(", ") || "N/A";
    },

    getDniInfo(patient) {
      return patient?.governmentId?.number
        ? `${patient.governmentId.type.toUpperCase()} ${
            patient.governmentId.number
          }`
        : "";
    },
    getHealthInsurance(patient) {
      // Nueva estructura
      if (patient?.patient?.medicalInsuranceObj?.number) {
        const plan = patient.patient.medicalInsuranceObj?.plan || "";
        const number = patient.patient.medicalInsuranceObj.number;
        return plan && number ? `${plan} ${number}` : "";
      }
      if (patient?.patient?.medicalInsuranceObj?.publicInsurance?.publicId) {
        const plan = patient.patient.medicalInsuranceObj?.plan || "";
        const number =
          patient.patient.medicalInsuranceObj.publicInsurance.publicId;
        return plan && number ? `${plan} ${number}` : "";
      }
      // Fallback a estructura antigua
      if (
        patient?.patient?.medicalInsurancePlan &&
        patient?.patient?.medicalInsuranceNumber
      ) {
        return `${patient.patient.medicalInsurancePlan} ${patient.patient.medicalInsuranceNumber}`;
      }
      return "";
    },

    getAppointmentHour(appointment) {
      return appointment?.startsAt
        ? moment(appointment.startsAt).format("HH:mm a")
        : "N/A";
    },

    goToPatientDashboard(id) {
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "patient-dashboard",
          params: { id },
        })
      );
    },

    async handleSlotStatus(slot) {
      if (
        slot?.appointment?.status !== "pending" &&
        slot?.appointment?.status !== "started" &&
        slot?.appointment?.status !== "admitted"
      ) {
        this.goToPatientDashboard(slot?.appointment?.patient?._id);
        return;
      }

      if (
        this.isAdmissionForDeragopyanEnabled &&
        this.isDirectAdmissionEnabled &&
        slot?.appointment?.status === "pending"
      ) {
        await this.handleDirectAdmission(slot);
        return;
      }

      this.handleSlot(slot);
      this.$message.success("Turno iniciado con éxito");
    },

    async handleDirectAdmission(slot) {
      if (!this.isAdmissionForDeragopyanEnabled) return;
      if (!slot?.appointment?._id) return;

      try {
        const appointment = await appointmentService.admitAppointment(
          slot.appointment._id
        );
        const slotIndex = this.slots.findIndex((s) => s._id === slot._id);
        if (slotIndex !== -1 && this.slots[slotIndex].appointment) {
          this.$set(
            this.slots[slotIndex].appointment,
            "status",
            appointment.status
          );
        }

        this.$message.success("Turno admitido con éxito");
      } catch (error) {
        // Error handling is done by the service
      }
    },
    canReAdmit(slot) {
      if (slot?.appointment?.status !== "done") return false;
      return moment(slot.appointment.startsAt).isSame(moment(), "day");
    },

    handleReAdmission(slot) {
      this.handleSlot(slot);
    },

    isSlotClosed(slot) {
      return (
        slot?.appointment?.status !== "pending" &&
        slot?.appointment?.status !== "started" &&
        slot?.appointment?.status !== "admitted"
      );
    },
    isDisabledStyle(slot) {
      if (
        slot?.appointment?.status !== "pending" &&
        slot?.appointment?.status !== "started" &&
        slot?.appointment?.status !== "admitted"
      ) {
        return "disabled";
      } else {
        return "";
      }
    },

    getAppointmentStatus(slot) {
      return slot?.appointment?.status || "pending";
    },

    getStatusInfo(slot) {
      const status = this.getAppointmentStatus(slot);
      return getPatientAdmissionStatus(status);
    },

    getButtonText(slot) {
      if (!slot?.appointment) return "Atender";
      const statusInfo = this.getStatusInfo(slot);
      return statusInfo.label;
    },

    getButtonClass(slot) {
      if (!slot?.appointment) return "";
      const statusInfo = this.getStatusInfo(slot);
      return statusInfo.buttonClass || "button-glass-new";
    },

    isAppointmentAdmitted(slot) {
      return slot?.appointment?.status === "admitted";
    },

    isAttendButtonDisabled(slot) {
      if (this.isSlotClosed(slot)) return true;
      if (
        this.isAdmissionForDeragopyanEnabled &&
        slot?.appointment?.status === "pending"
      )
        return true;
      return false;
    },

    getAttendTooltip(slot) {
      if (
        this.isAdmissionForDeragopyanEnabled &&
        slot?.appointment?.status === "pending"
      ) {
        return "El paciente debe ser admitido para iniciar la consulta";
      }
      return "";
    },

    async refreshSlotsStatus() {
      try {
        const startOfDay = moment(this.selectedDate)
          .startOf("day")
          .toISOString();
        const endOfDay = moment(this.selectedDate).endOf("day").toISOString();

        const slots = await slotService.getSlotsByDoctor({
          doctorId: this.doctorId(),
          params: {
            startsAt: { $gte: startOfDay },
            endsAt: { $lte: endOfDay },
            ...(this.filterByOccupied && { appointment: { $exists: true } }),
            populate: [
              { path: "appointment", populate: "patient" },
              { path: "medicalSpecialties institution doctor" },
            ],
            offset: this.pagination.offset,
            limit: this.pagination.limit,
          },
        });

        slots.docs.forEach((updatedSlot) => {
          const index = this.slots.findIndex((s) => s._id === updatedSlot._id);
          if (
            index !== -1 &&
            this.slots[index].appointment &&
            updatedSlot.appointment
          ) {
            this.$set(
              this.slots[index].appointment,
              "status",
              updatedSlot.appointment.status
            );
          }
        });
      } catch {
        // Silent refresh - errors don't interrupt the doctor's workflow
      }
    },

    isCallButtonLoading(slot) {
      if (!slot?.appointment?._id) return false;
      return this.loadingCalls[slot.appointment._id] || false;
    },

    async checkCallProviderEnabled() {
      this.isCallProviderEnabled =
        await parametrizationService.isBooleanEnabled("CALL_PROVIDER_ENABLED");
    },

    async checkDirectAdmissionEnabled() {
      if (!this.isAdmissionForDeragopyanEnabled) {
        this.isDirectAdmissionEnabled = false;
        return;
      }

      this.isDirectAdmissionEnabled =
        await parametrizationService.isBooleanEnabled(
          "DIRECT_ADMISSION_ENABLED"
        );
    },

    async handleCallPatient(slot) {
      if (!slot?.appointment?._id) return;

      const appointmentId = slot.appointment._id;
      this.$set(this.loadingCalls, appointmentId, true);

      try {
        await appointmentService.callPatient(appointmentId);
        this.$message.success("Llamada enviada exitosamente");
      } catch (error) {
        // Error handling is done by the service
      } finally {
        this.$set(this.loadingCalls, appointmentId, false);
      }
    },
  },
};
</script>

<template lang="pug">
  section
    BaseSubNav
      template(#title)
        | Mis atenciones del día
        small(v-if="pagination.total")  ({{ pagination.total }})
    .box.box--with-subnav
      spinner(v-if="isLoading")
      div.slot-wrapper(v-if="!isLoading && !filteredSlots.length")
        div.date-navigation
            micon.slot-arrow(:name="'chevron_left'" @click.native="changeDate(-1)")
            button.date-display(type="button" @click="openDatePicker")
              h3 {{ formattedDate }}
              micon.date-display__icon(:name="'calendar'")
            micon.slot-arrow(:name="'chevron_right'" @click.native="changeDate(1)")
            button.button-glass-new.today-button(v-if="!isToday" type="button" @click="goToToday") Hoy
            el-date-picker.date-navigation__picker(
              ref="datePicker"
              v-model="selectedDate"
              type="date"
              format="dd/MM/yyyy"
              :clearable="false"
              :editable="false"
            )
        h1.no-slots No hay citas

      div.slot-wrapper(v-if="!isLoading && filteredSlots.length")
        div.date-navigation
            micon.slot-arrow(:name="'chevron_left'" @click.native="changeDate(-1)")
            button.date-display(type="button" @click="openDatePicker")
              h3 {{ formattedDate }}
              micon.date-display__icon(:name="'calendar'")
            micon.slot-arrow(:name="'chevron_right'" @click.native="changeDate(1)")
            button.button-glass-new.today-button(v-if="!isToday" type="button" @click="goToToday") Hoy
            el-date-picker.date-navigation__picker(
              ref="datePicker"
              v-model="selectedDate"
              type="date"
              format="dd/MM/yyyy"
              :clearable="false"
              :editable="false"
            )

        div.slot-container(v-for="slot in filteredSlots" :key="slot._id" :class="{ 'slot-container--instant': slot.appointment && slot.appointment.isInstant }")
          div.instant-badge(v-if="slot.appointment && slot.appointment.isInstant") Sobreturno

          div.slot-container__left
            div.time-specialty
              .slot-time
                micon(name="clock")
                h1 {{ getAppointmentHour(slot) }}
              .slot-specialty-container
                button.button-glass-new {{ getAttentionType(slot) }}

            div.patient-data-container
              div.patient-data
                .name-age
                  h1 {{ slot.available ? "Libre" : slot.appointment ? getFullName(slot.appointment.patient) : "No disponible" }}
                  p {{ slot.appointment.patient.bornAt | age }}
                  .admission-chip(v-if="isAdmissionTimestampEnabled && slot.appointment && slot.appointment.admittedAt")
                    micon(name="check")
                    span Admitido {{ slot.appointment.admittedAt | formatDateAndTime }}
                p {{ getDniInfo(slot.appointment.patient) }} | {{ getHealthInsurance(slot.appointment.patient) }}
                div.observation-box(v-if="isObservationsEnabled && slot.appointment.observation")
                  span.observation-box__label Observaciones:
                  span.observation-box__value {{ truncateObservation(slot.appointment.observation) }}
                .patient-buttons
                  button.button-blue-new(@click="goToPatientDashboard(slot.appointment.patient._id)") Historia clínica
                  button.button-blue-new(v-if="isImageReportsButtonVisible && patientImageResultsMap[slot.appointment.patient._id]" @click="openImageReports(slot)") Informes
                  //- Botón "Archivos adjuntos" oculto en sala de espera

          div.slot-container__right
            button.button-call(
              v-if="slot.appointment && isCallProviderEnabled"
              @click="handleCallPatient(slot)"
              :disabled="!isAppointmentAdmitted(slot) || isCallButtonLoading(slot)"
              :class="{ 'loading': isCallButtonLoading(slot), 'disabled': !isAppointmentAdmitted(slot) }"
            )
              micon(name="phone_iphone")
              | {{ isCallButtonLoading(slot) ? 'Llamando...' : 'LLAMAR' }}
            
            button.status-button(
              v-if="slot.appointment"
              :class="[getButtonClass(slot), (isSlotClosed(slot) && !canReAdmit(slot)) ? 'disabled' : '']"
              @click="canReAdmit(slot) ? handleReAdmission(slot) : handleSlotStatus(slot)"
              :disabled="isSlotClosed(slot) && !canReAdmit(slot)"
            )
              micon(:name="slot.appointment.attentionType === 'virtual' ? 'videocam' : 'door-enter'")
              | {{ getButtonText(slot) }}

              
    pagination(
      :isLoading="isLoading"
      :limit="pagination.limit"
      :total="pagination.total"
      @pagination="setPagination"
    )

    modal-appointment(
      v-if='this.isModalVisible'
      :show-dialog-modal='this.isModalVisible'
      :selected-slot='selectedSlot'
      @close="closeModal"
    )

    modal-image-reports(
      :visible.sync="imageReportsModal.visible"
      :patient-id="imageReportsModal.patientId"
      :patient-name="imageReportsModal.patientName"
    )
</template>

<style lang="scss" scoped>
.no-slots {
  text-align: center;
  padding: 50px;
  font-size: 2em;
  width: 100%;
}

.disabled {
  background-color: $light-gray !important;
  cursor: not-allowed !important;
}

.in-person,
.virtual {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 115px;
  height: 60px;
  border-radius: 10px;
  color: $white;
  border: none;

  svg {
    fill: $white;
  }
}

.in-person {
  background: $primary;

  svg {
    height: 20px;
  }

  &:hover {
    opacity: 0.9;
    background-color: $steel-blue;
    color: $white;
  }
}

.virtual {
  background: $leaf-green;

  &:hover {
    opacity: 0.9;
    background-color: $leaf-green;
    color: $white;
  }
}

.slot-wrapper {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 5px 20px;

  .date-navigation {
    position: relative;
    display: flex;
    justify-content: center;
    flex-direction: row;
    align-items: center;
    margin: 10px 0px;

    .date-display {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin: 0px 20px;
      padding: 6px 12px;
      background: transparent;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: background-color 150ms ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      h3 {
        margin: 0;
        min-width: 120px;
        color: $midnight-blue;
        font-weight: 400;
        font-size: 16px;
        text-align: center;
      }

      .date-display__icon {
        fill: $midnight-blue;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
      }
    }

    .slot-arrow {
      fill: $midnight-blue;
      cursor: pointer;
    }

    .today-button {
      margin-left: 16px;
      padding: 4px 14px;
    }

    .date-navigation__picker {
      position: absolute;
      left: 50%;
      bottom: 0;
      width: 0;
      height: 0;
      padding: 0;
      overflow: hidden;
      opacity: 0;
      pointer-events: none;
    }
  }

  .slot-container {
    padding: 20px 7%;
    border-radius: 10px;
    display: flex;
    justify-content: space-between;
    box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.15);
    border: 2px solid transparent;

    &--instant {
      position: relative;
      background-color: rgb(255 255 156 / 54%);
      box-shadow: inset 6px 0 0 $corn, 0 0 6px 0 rgba(0, 0, 0, 0.15);
      border-color: transparent;
    }

    .instant-badge {
      position: absolute;
      top: -10px;
      left: 20px;
      background-color: $corn;
      color: $white;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 4px 12px;
      border-radius: 12px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    }

    &__left,
    &__right {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    &__left {
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      width: 70%;
    }

    &__right {
      width: 30%;
      display: flex;
      align-items: flex-end;
      justify-content: center;
    }

    .time-specialty {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;
      width: 90px;

      .slot-time {
        display: flex;
        align-items: center;
        padding: 2px 7px;
        background-color: $light-gray;
        border-radius: 10px;
        white-space: nowrap;

        svg {
          height: 65%;
          width: 20px;
        }

        path {
          stroke-width: 0.5px;
          fill: none;
        }
      }

      h1 {
        color: $midnight-blue;
        font-size: 15px;
        font-weight: 500;
        margin-bottom: 0px;
      }

      .div2Nieto {
        display: flex;
        flex-direction: row;
      }

      .slot-specialty-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        margin: 20px 20px 0 0;

        .slot-attentionType {
          background: none;
          padding: 5px 13px;
          border-radius: 7px;
          border: none;
          box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.25);
          color: $midnight-blue;
        }

        .slot-specialty {
          background: none;
          padding: 5px 13px;
          border-radius: 7px;
          margin: 2px 0;
          border: none;
          box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.25);
        }
      }
    }

    .patient-data {
      height: 100%;

      p {
        margin: 0;
        font-size: 11px;
        color: $black;
      }

      .name-age {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;

        h1 {
          margin: 0px 10px 5px 0;
          font-weight: 400;
        }

        p {
          color: $botticelli;
          margin: 0;
          align-self: center;
          font-size: 13px;
        }

        .admission-chip {
          margin-left: auto;
          margin-top: 0;
        }
      }

      .patient-buttons {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        margin-top: 20px;
      }
    }

    .patient-data-container {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-start;
      height: 100%;
      width: 100%;
    }
  }
}

.observation-box {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: $white;
  border: 2px solid $gainsboro;
  padding: 10px 14px;
  border-radius: 14px;
  margin-top: 8px;

  &__label {
    font-size: 14px;
    font-weight: 700;
    color: $midnight-blue;
    flex-shrink: 0;
  }

  &__value {
    font-size: 14px;
    font-weight: 600;
    color: #000;
    line-height: 1.3;
  }
}

.admission-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 3px 10px;
  background-color: rgba(19, 170, 34, 0.12);
  color: $leaf-green;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  width: fit-content;

  svg {
    width: 12px;
    height: 12px;
    fill: $leaf-green;
  }
}

.admission-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 3px 10px;
  background-color: rgba(19, 170, 34, 0.12);
  color: $leaf-green;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  width: fit-content;

  svg {
    width: 12px;
    height: 12px;
    fill: $leaf-green;
  }
}
</style>
